import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router";
import { useBeforeUnload, useBlocker } from "react-router-dom";
import Alert from "@shared/v2/Alert";
import Button from "@shared/v2/Button";
import TextButton from "@shared/v2/TextButton";
import { useDispatch, useStore } from "react-redux";
import PageFlowHeader from "../shared/PageFlowHeader";
import PageDetailsStep from "./PageDetailsStep";
import ConfirmLeaveModal from "./ConfirmLeaveModal";
import { isTokenValid } from "./api";

import {
  addPage,
  updatePage,
  updatePageSettings,
  getPageTemplates,
  getPageSettings,
  getSites,
  getSiteMlsIds,
  getToken,
  getPageContentComponents,
  updateContentComponentBindings,
  updateContentComponent,
  getComponents,
} from "../../reducers/websiteReducer/areaPageReducer";
import {
  useAddPageError,
  useAddPageLoading,
  useAreaPageTemplateUuid,
  useSiteUuid,
  useSitesLoading,
  useToken,
  useTokenExpiresAt,
  useMlsIds,
} from "../../reducers/websiteReducer/areaPageSelectors";

const NewAreaPage = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const store = useStore();
  // Finish completes the flow (goes to the editor); any other exit prompts the Confirm Leave modal.
  const finishing = useRef(false);
  const pageError = useAddPageError();
  const pageLoading = useAddPageLoading();
  const token = useToken();
  const tokenExpiresAt = useTokenExpiresAt();
  const siteUuid = useSiteUuid();
  const sitesLoading = useSitesLoading();
  const templateUuid = useAreaPageTemplateUuid();
  const mlsIds = useMlsIds();

  // "" until an option is picked; the empty string is what keeps Finish gated (item 7).
  const [area, setArea] = useState("");
  const [title, setTitle] = useState("");
  const [slug, setSlug] = useState("");
  // Set once addPage succeeds so a retry after a later failure finishes this page instead of
  // creating another one.
  const createdUuid = useRef(null);

  useEffect(() => {
    const load = async () => {
      if (!isTokenValid(token, tokenExpiresAt)) {
        await dispatch(getToken()).unwrap();
      }
    };
    load();
  }, []);

  useEffect(() => {
    if (!token) return;

    const load = async () => {
      await Promise.all([
        siteUuid ? null : dispatch(getSites()).unwrap(),
        dispatch(getPageTemplates()).unwrap(),
      ]);
      // On a fresh mount (direct URL) only the index has fetched these; needs organizationUuid from getSites.
      if (!mlsIds) dispatch(getSiteMlsIds());
    };
    load();
  }, [token]);

  const updateFeaturedListingComponent = async (selectedArea, featuredListingsComponent) => {
    const splittedValue = selectedArea.value.split(",");
    let streetAddress = [];
    let postalCodes = [];
    let cityStates = [];
    let neighborhoodCityStates = [];
    let countyStates = [];

    switch (selectedArea.type) {
      case "address":
        streetAddress = [splittedValue[0]];
        break;
      case "postalCode":
        postalCodes = [splittedValue[0]?.trim()];
        break;
      case "city":
        cityStates = [
          {
            city: splittedValue[0]?.trim(),
            state: splittedValue[1]?.trim(),
          },
        ];

        break;
      case "neighborhood":
        neighborhoodCityStates = [
          {
            neighborhood: splittedValue[0]?.trim(),
            city: splittedValue[1]?.trim(),
            state: splittedValue[2]?.trim(),
          },
        ];
        break;
      case "schoolDistrict":
        break;
      case "county":
        countyStates = [
          {
            county: splittedValue[0]?.trim(),
            state: splittedValue[1]?.trim(),
          },
        ];
        break;

      default:
        break;
    }

    const payload = {
      config: {
        ...featuredListingsComponent.bindings[0].config,
        vars: {
          ...featuredListingsComponent.bindings[0].config.vars,
          requestBody: {
            ...featuredListingsComponent.bindings[0].config.vars.requestBody,
            streetAddress,
            postalCodes,
            cityStates,
            neighborhoodCityStates,
            countyStates,
            mlsIds: mlsIds || [],
          },
        },
      },
    };

    const contentComponentUuid = featuredListingsComponent?.uuid;
    const bindingUuid = featuredListingsComponent?.bindings?.[0]?.uuid;
    await dispatch(updateContentComponentBindings({ contentComponentUuid, bindingUuid, payload })).unwrap();
  };

  const updateNeighborhoodComponent = async (selectedArea, neighborhoodComponent) => {
    const payload = {
      content: {
        latitude: selectedArea?.lat,
        longitude: selectedArea?.lng,
      },
    };

    const { uuid: componentUuid } = neighborhoodComponent;
    await dispatch(updateContentComponent({ uuid: componentUuid, payload })).unwrap();
  };

  // getSites resolves siteUuid to 0 when the account has no main site.
  const noMainSite = siteUuid === 0 && !sitesLoading;
  const readyToCreate = Boolean(siteUuid && templateUuid);

  const unmounted = useRef(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    unmounted.current = false;
    return () => {
      unmounted.current = true;
    };
  }, []);

  useBeforeUnload(
    useCallback((e) => {
      e.returnValue = "Are you sure you want to cancel creating your new page?";
    }, []),
    { capture: true },
  );

  const blocker = useBlocker(
    ({ currentLocation, nextLocation }) =>
      !finishing.current && currentLocation.pathname !== nextLocation.pathname,
  );

  const onFinishClicked = async () => {
    // The MLS area is required (MVP feedback): custom area-less pages are out.
    if (title === "" || area === "" || !readyToCreate || saving) return;
    finishing.current = true;
    setSaving(true);
    setError("");
    try {
      // The form can sit open longer than the token's 5 minutes; refresh before creating.
      if (!isTokenValid(token, tokenExpiresAt)) {
        await dispatch(getToken()).unwrap();
      }
      if (!createdUuid.current) {
        const newPage = await dispatch(addPage({ area, title, slug })).unwrap();
        createdUuid.current = newPage?.data?.uuid;
      } else {
        // Retry after a partial failure: the form stayed editable, so carry any title
        // and slug edits onto the already-created page instead of keeping the originals.
        await dispatch(updatePage({ name: title, slug, path: `/${slug}` })).unwrap();
      }
      const newUuid = createdUuid.current;
      const pageSettings = await dispatch(getPageSettings({ uuid: newUuid })).unwrap();
      const payload = {
        settings: pageSettings.data.settings,
        seoTitle: pageSettings.data.seoTitle,
        seoDescription: pageSettings.data.seoDescription,
        status: pageSettings.data.status,
        variables: {
          vars: [
            {
              key: "location",
              label: "Location",
              value: area.label,
            },
          ],
          version: 1,
        },
      };
      await dispatch(updatePageSettings(payload)).unwrap();
      await dispatch(getComponents()).unwrap();
      await dispatch(getPageContentComponents()).unwrap();

      // The reducer merges the fetched components with their schemas; read the merged result.
      const { pageContentComponents } = store.getState().areaPage;
      const featuredListingsComponent = pageContentComponents?.find(
        (component) => component.name === "Featured Properties",
      );
      const neighborhoodComponent = pageContentComponents?.find(
        (component) => component.name === "Neighborhood",
      );
      await updateFeaturedListingComponent(area, featuredListingsComponent);
      await updateNeighborhoodComponent(area, neighborhoodComponent);

      navigate(`/area_pages/${newUuid}`);
    } catch {
      // Re-arm the leave blocker and let the user retry.
      finishing.current = false;
      if (!unmounted.current) setError("The page couldn't be created. Please try again.");
    } finally {
      if (!unmounted.current) setSaving(false);
    }
  };

  return (
    <>
      <div className="tw-flex-1 tw-max-w-[1416px] tw-mx-auto tw-flex tw-gap-[32px]">
        <div className="tw-relative tw-w-full tw-flex tw-flex-col tw-gap-[16px] tw-m-[16px] tw-bg-white tw-border-[1px] tw-border-solid tw-border-gray-10 tw-rounded-[8px] tw-py-[12px] tw-px-[24px] tw-overflow-auto">
          <PageFlowHeader
            title="New Area Page"
            actions={
              <>
                {pageError && <p>{pageError}</p>}
                <TextButton size="medium" onClick={() => navigate("/area_pages")} disabled={pageLoading}>
                  Cancel
                </TextButton>
                <Button
                  schema="primary"
                  size="medium"
                  className="tw-whitespace-nowrap"
                  onClick={onFinishClicked}
                  disabled={!title || !area || !readyToCreate || saving}
                  isLoading={saving}
                >
                  Finish
                </Button>
              </>
            }
          />
          {noMainSite && (
            <Alert
              variant="error"
              text="No main website was found for this account, so a page can't be created."
            />
          )}
          {error && <Alert variant="error" text={error} />}
        </div>
      </div>

      <PageDetailsStep onAreaChanged={setArea} onTitleChanged={setTitle} onSlugChanged={setSlug} />

      <ConfirmLeaveModal blocker={blocker} />
    </>
  );
};

export default NewAreaPage;
